import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Button,
  DragDropFileList,
  DrawerStepper,
  Label,
  Radio,
  Select,
  Text,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import {
  onSuccessMessage,
  onErrorMessage,
  getFileNameAndExtension,
  formatDate,
} from '@/helpers'

import {
  useUpdateProfile,
  QUERIES_ADMIN,
  FINANCE_PROFILE_URL,
  api,
} from '@/services'

import { ProfileProps, ProfileTaxStatusEnum } from '@/types'

import { TaxInformationDrawerProps } from './tax-information-drawer.type'
import styles from './tax-information-drawer.module.css'

export const TaxInformationDrawer = ({
  isOpen,
  onHandleOpen,
  profile,
}: TaxInformationDrawerProps) => {
  const { t } = useTranslation('screenProfileShow')
  const { t: tEnums } = useTranslation('enums')

  const queryClient = useQueryClient()

  const [useCFDI, setUseCFDI] = useState(profile?.financeProfile?.cfdi)
  const [taxStatus, setTaxStatus] = useState<keyof typeof ProfileTaxStatusEnum>(
    profile?.taxStatus || 'pending',
  )

  const { mutateAsync: mutateAsyncUpdateProfile } = useUpdateProfile()

  const onSubmitCompleted = async () => {
    const payload = {
      uuid: profile?.uuid,
      cfdi: useCFDI,
      taxStatus,
    }

    const response = await mutateAsyncUpdateProfile(
      payload as Partial<ProfileProps>,
    )

    if (response.success) {
      onSuccessMessage(
        t('profileFormSuccessUpdateTitle'),
        t('profileFormSuccessUpdateMessage'),
      )

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.PROFILE.module],
      })

      onHandleOpen(false)
    } else {
      onErrorMessage(
        t('profileFormErrorUpdateTitle'),
        t('profileFormErrorUpdateMessage'),
      )
    }
  }

  const getFileValue = (file: string | undefined) => {
    if (!file) {
      return []
    }

    const { fileName, extension } = getFileNameAndExtension(file)

    return [
      {
        id: new Date().getTime(),
        name: `${fileName}.${extension}`,
        url: file as string,
        uploaded: true,
      },
    ]
  }

  const onSuccessUpload = () => {
    queryClient.invalidateQueries({
      queryKey: ['profile'],
    })
  }

  return (
    <DrawerStepper
      translations={{
        optional: t('profileFormOptional'),
      }}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      title={t('profileTaxInformationDraweTitle')}
      description={t('profileTaxInformationDraweSubtitle')}
      initialStep="root"
      steps={{
        root: {
          isOptional: false,
          backNavigate: 'root',
          component: () => (
            <fieldset className={styles.core}>
              <div className={styles['document-container']}>
                <Label>{t('profileTaxInformationTaxSituationLabel')}</Label>

                <Select
                  placeholder={t('selectPlaceholder')}
                  options={[
                    {
                      label: tEnums('profile_tax_status_pending'),
                      value: 'pending',
                    },
                    {
                      label: tEnums('profile_tax_status_review'),
                      value: 'review',
                    },
                    {
                      label: tEnums('profile_tax_status_completed'),
                      value: 'completed',
                    },
                    {
                      label: tEnums('profile_tax_status_invalid'),
                      value: 'invalid',
                    },
                  ]}
                  value={taxStatus}
                  onChange={(value) =>
                    setTaxStatus(value as keyof typeof ProfileTaxStatusEnum)
                  }
                />
              </div>

              <div className={styles['document-container']}>
                <Label>{t('profileTaxInformationCfdiUsageLabel')}</Label>

                <Radio
                  value={useCFDI === 'g01'}
                  onChange={() => setUseCFDI('g01')}
                >
                  {tEnums('profile_cfdi_G01')}
                </Radio>

                <Radio
                  value={useCFDI === 'g03'}
                  onChange={() => setUseCFDI('g03')}
                >
                  {tEnums('profile_cfdi_G03')}
                </Radio>
              </div>

              <div className={styles['document-container']}>
                <Text className={styles['document-label']}>
                  {t('formDocumentsFiscalSituationLabel')}
                </Text>

                <DragDropFileList
                  axiosInstance={api}
                  translations={{
                    clickToUpload: t('uploadButton'),
                    dragFile: t('uploadDrag'),
                    sizeFile: t('uploadSize'),
                  }}
                  initialData={getFileValue(profile?.taxSituation)}
                  urlAdd={FINANCE_PROFILE_URL(profile?.uuid || '')}
                  configsRequest={{
                    fileField: 'tax_situation_file',
                    method: 'PATCH',
                  }}
                  successedUpload={onSuccessUpload}
                  onClickPreview={(url) => window.open(url, '_blank')}
                />
              </div>

              {profile?.taxStatusCompletedAt && (
                <div className={styles.evaluation}>
                  <Text>
                    {t('profileTaxInformationTaxStatusValidByLabel')}:
                  </Text>
                  <Text>{`${profile?.taxStatusCompletedBy?.name} - ${formatDate(profile?.taxStatusCompletedAt)}`}</Text>
                </div>
              )}

              <div className={styles['document-container']}>
                <Text className={styles['document-label']}>
                  {t('formDocumentsResidenceLabel')}
                </Text>

                <DragDropFileList
                  axiosInstance={api}
                  translations={{
                    clickToUpload: t('uploadButton'),
                    dragFile: t('uploadDrag'),
                    sizeFile: t('uploadSize'),
                  }}
                  initialData={getFileValue(profile?.addressProof)}
                  urlAdd={FINANCE_PROFILE_URL(profile?.uuid || '')}
                  configsRequest={{
                    fileField: 'address_proof_file',
                    method: 'PATCH',
                  }}
                  successedUpload={onSuccessUpload}
                  onClickPreview={(url) => window.open(url, '_blank')}
                />
              </div>

              <div className={styles['document-container']}>
                <Text className={styles['document-label']}>
                  {t('formDocumentsSatLabel')}
                </Text>

                <DragDropFileList
                  axiosInstance={api}
                  translations={{
                    clickToUpload: t('uploadButton'),
                    dragFile: t('uploadDrag'),
                    sizeFile: t('uploadSize'),
                  }}
                  initialData={getFileValue(profile?.taxComplianceCertificate)}
                  urlAdd={FINANCE_PROFILE_URL(profile?.uuid || '')}
                  configsRequest={{
                    fileField: 'tax_compliance_certificate_file',
                    method: 'PATCH',
                  }}
                  successedUpload={onSuccessUpload}
                  onClickPreview={(url) => window.open(url, '_blank')}
                />
              </div>
            </fieldset>
          ),
          footer: () => (
            <>
              <Button variant="secondary" onClick={() => onHandleOpen(false)}>
                {t('profileFormNavigateButtonBack')}
              </Button>

              <Button onClick={() => onSubmitCompleted()}>
                {t('profileFormNavigateButtonFinish')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
